import { Spinner, Textarea } from "@fluentui/react-components";
import { connect } from "react-redux";
import { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";

// component functions
import * as ComponentFunctions from './Debt.Form.Functions';
import { suggestedCurrencies, validateDebtForm } from "../../CashFlow.Form.Logic";
import { AmountInput, CurrencyChips, DateInput, MoreData, PartyChip, SaveErrorBanner, Step } from "../../CashFlow.Form.Bits";
import { GuideDrawer, GuideDrawerForm } from "../../../../theme/components/GuideDrawer";
import { GuideField } from "../../../../theme/components/GuideField";
import { MEDICAL_CENTER_NAME } from "../../General/Cashflow.General.Functions";

// components
import PersonSelectorComponent from "../../../../commons/component/Inputs/PersonSelector/PersonSelector.Component";
import ProtocolSelectorComponent from "../../../../commons/component/Inputs/ProtocolSelector/ProtocolSelectorComponent";
import { formatCurrencyAmountWithIssue } from "../../../../commons/currency/Currency.Functions";

// models
import { Person } from "../../../../models/Entities/Person/Person";
import { Protocol } from "../../../../models/Entities/Protocols/Protocol";
import { NewDebt } from "../../../../models/Entities/CashFlow/Debt/NewDebt";
import { Debt } from "../../../../models/Entities/CashFlow/Debt/Debt";

/**
 * Alta y edición de una deuda. Una de las dos partes es siempre el centro, así
 * que sólo se pregunta por la contraparte; moneda y fecha vienen sugeridas.
 */
const DebtFormComponent = (props: ComponentFunctions.Props) => {

    const isReceivable = props.FormType === ComponentFunctions.FormType.Creditor;
    const isEdit = !!props.CurrentDebt;

    // ── Estado del formulario ──
    const [counterpart, setCounterpart] = useState<Person | undefined>(undefined);
    const [currency, setCurrency] = useState<string | undefined>(undefined);
    const [amountText, setAmountText] = useState<string>('');
    const [amount, setAmount] = useState<number>(0);
    const [date, setDate] = useState<Date | undefined>(new Date());
    const [protocolId, setProtocolId] = useState<number | undefined>(undefined);
    const [comments, setComments] = useState<string>('');
    const [submitted, setSubmitted] = useState<boolean>(false);
    const [touched, setTouched] = useState<boolean>(false);

    const ownerPerson: Person = { personIdentifier: props.OwnerId, id: 0, isDeleted: false, personType: 0, businessName: MEDICAL_CENTER_NAME } as Person;

    // ── Carga ──
    useEffect(() => {
        if (!props.isOpen) return;
        props.GetAllPersons();
        if (props.DebtId && !props.CurrentDebt) props.GetDebtById(props.DebtId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isOpen, props.DebtId]);

    // Al abrir: arranca con lo que ya se sabe (contraparte fija o filtrada, hoy).
    useEffect(() => {
        if (!props.isOpen) return;
        setSubmitted(false);
        setTouched(false);
        if (props.CurrentDebt) return;
        const prefill = props.FixedCounterpart
            ?? (props.PrefillCounterpartId ? ComponentFunctions.GetPersonByIdentifier(props.Persons, props.OwnerId, props.PrefillCounterpartId) : undefined);
        setCounterpart(prefill);
        setCurrency(undefined);
        setAmountText('');
        setAmount(0);
        setDate(new Date());
        setProtocolId(undefined);
        setComments('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isOpen, props.FixedCounterpart?.personIdentifier, props.PrefillCounterpartId]);

    // Edición: se completa con la deuda.
    useEffect(() => {
        const debt = props.CurrentDebt;
        if (!debt) return;
        const counterpartId = isReceivable ? debt.debtor : debt.creditor;
        setCounterpart(ComponentFunctions.GetPersonByIdentifier(props.Persons, props.OwnerId, counterpartId));
        setCurrency(debt.currencyCode);
        setAmount(debt.originalAmount);
        setAmountText(String(debt.originalAmount));
        setDate(debt.debtDate ? new Date(debt.debtDate) : undefined);
        setProtocolId(debt.protocolId ?? undefined);
        setComments(debt.comments || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.CurrentDebt?.id, props.Persons.length]);

    useEffect(() => {
        if (props.isSaved && submitted) {
            setSubmitted(false);
            props.onClose();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isSaved]);

    // Segundo camino de cierre: terminó de guardar (el spinner bajó) sin error.
    // No depende de que la bandera de éxito cambie de valor, que es lo que
    // dejaba el drawer abierto cuando ya venía en true de un guardado anterior.
    const wasSavingRef = useRef(false);
    useEffect(() => {
        const finished = wasSavingRef.current && !props.isSaving;
        wasSavingRef.current = props.isSaving;
        if (finished && submitted && !props.failOnSaving) {
            setSubmitted(false);
            props.onClose();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isSaving, props.failOnSaving]);

    useEffect(() => {
        if (props.failOnSaving) setSubmitted(false);
    }, [props.failOnSaving]);

    // ── Sugerencias ──
    const suggested = useMemo(
        () => suggestedCurrencies({ counterpartId: counterpart?.personIdentifier, debts: props.AllDebts, payments: props.AllPayments }),
        [counterpart?.personIdentifier, props.AllDebts, props.AllPayments]
    );

    useEffect(() => {
        // La moneda se propone sola; si el usuario ya eligió una, se respeta.
        if (!isEdit && !touched) setCurrency(suggested[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [suggested.join(','), isEdit]);

    // ── Validación ──
    const errors = validateDebtForm({ counterpartId: counterpart?.personIdentifier, currency, amount, date });
    const showError = (key: string) => (submitted ? errors[key] : undefined);
    const canSave = Object.keys(errors).length === 0 && !props.isSaving;

    // ── Guardar ──
    const save = () => {
        setSubmitted(true);
        if (Object.keys(errors).length > 0 || !counterpart || !currency || !date) return;

        const debtor = isReceivable ? counterpart.personIdentifier : props.OwnerId;
        const creditor = isReceivable ? props.OwnerId : counterpart.personIdentifier;

        if (!props.CurrentDebt) {
            const newDebt: NewDebt = {
                creditor, debtor, debtDate: date, originalAmount: amount, currencyCode: currency,
                comments: comments || undefined, protocolId: protocolId ?? null
            };
            props.CreateDebt(newDebt);
        } else {
            const debt: Debt = { ...props.CurrentDebt, originalAmount: amount, protocolId: protocolId ?? null, comments: comments || undefined };
            props.UpdateDebt(debt);
        }
    };

    const close = () => { setSubmitted(false); props.onClose(); };

    // ── Textos según el sentido de la deuda ──
    const title = isEdit ? 'Editar deuda' : (isReceivable ? 'Nueva deuda a cobrar' : 'Nueva deuda a pagar');
    const whoQuestion = isReceivable ? '¿Quién nos debe?' : '¿A quién le debemos?';
    const counterpartRole = isReceivable ? 'Deudor' : 'Acreedor';
    const centerRole = isReceivable ? 'Acreedor' : 'Deudor';
    const counterpartName = counterpart ? props.NameOf(counterpart.personIdentifier) : undefined;
    const isCounterpartFixed = !!props.FixedCounterpart;

    const renderForm = () => (
        <GuideDrawerForm className="cff-root" testId="debt-form">
            {props.failOnSaving && (
                <SaveErrorBanner title="No se pudo guardar la deuda." message={props.error?.ErrorMessage || 'Probá de nuevo.'} items={props.error?.Errors} />
            )}

            {/* Con quién */}
            <Step title={whoQuestion} error={showError('counterpart')}
                hint={isEdit || isCounterpartFixed ? undefined : <>El centro es siempre el {centerRole.toLowerCase()}; sólo falta la otra parte.</>}>
                <div className="cff-parties">
                    {isEdit || isCounterpartFixed ? (
                        <PartyChip role={counterpartRole} name={counterpartName || '—'} />
                    ) : (
                        <div className="cff-parties__selector">
                            <PersonSelectorComponent
                                personSelected={counterpart ? [counterpart.personIdentifier] : []}
                                selectionLimit={1}
                                isRequired
                                fieldLabel={counterpartRole}
                                CustomAditionalPersonsList={[ownerPerson]}
                                onChangeSelection={(selected: Person[]) => setCounterpart(selected[0])}
                            />
                        </div>
                    )}
                    <span className="cff-parties__arrow" aria-hidden>{isReceivable ? '→' : '←'}</span>
                    <PartyChip role={centerRole} name={MEDICAL_CENTER_NAME} />
                </div>
            </Step>

            {/* Cuánto */}
            <Step title="¿Cuánto?"
                hint={!isEdit && counterpartName && suggested.length > 0 ? <>Moneda sugerida por lo que ya se movió con {counterpartName}.</> : undefined}>
                <GuideField label="Moneda" required error={showError('currency')}>
                    <CurrencyChips suggested={suggested} value={currency} onChange={(code) => { setTouched(true); setCurrency(code); }} disabled={isEdit} />
                </GuideField>
                <GuideField
                    label="Importe"
                    required
                    error={showError('amount')}
                    hint={isEdit && props.CurrentDebt
                        ? `Ya se pagaron ${formatCurrencyAmountWithIssue(props.CurrentDebt.paidAmount, currency)}; el importe no puede ser menor.`
                        : (amount > 0 && currency ? <span className="cff-num">{formatCurrencyAmountWithIssue(amount, currency)}</span> : undefined)}
                >
                    <AmountInput value={amountText} currency={currency} autoFocus={!isEdit && !!counterpart}
                        onChange={(text, value) => { setAmountText(text); setAmount(value); }} />
                </GuideField>
            </Step>

            {/* Cuándo */}
            <Step title="¿De qué fecha?">
                {isEdit ? (
                    <div className="cff-summary">
                        <div className="cff-summary__item"><div className="cff-summary__label">Fecha</div><div className="cff-summary__value cff-num">{date ? moment(date).format('DD/MM/YYYY') : '—'}</div></div>
                        {props.CurrentDebt?.settlementId && <div className="cff-summary__item"><div className="cff-summary__label">Liquidación</div><div className="cff-summary__value">LIQ-{props.CurrentDebt.settlementId}</div></div>}
                    </div>
                ) : (
                    <GuideField label="Fecha" required error={showError('date')} hint="Por defecto, hoy. La antigüedad de la deuda se cuenta desde acá.">
                        <DateInput value={date} onChange={setDate} />
                    </GuideField>
                )}
            </Step>

            <MoreData open={!!protocolId || !!comments}>
                <GuideField label="Protocolo" hint="Si la deuda pertenece a un estudio.">
                    <ProtocolSelectorComponent
                        selectionLimit={1}
                        selectedProtocols={protocolId === undefined ? [] : [{ id: protocolId } as Protocol]}
                        onChangeSelection={(selected: Protocol[] | undefined) => setProtocolId((selected || [])[0]?.id)}
                    />
                </GuideField>
                <GuideField label="Comentarios">
                    <Textarea value={comments} onChange={(_e, data) => setComments(data.value)} resize="vertical" />
                </GuideField>
            </MoreData>
        </GuideDrawerForm>
    );

    const loadingEdit = !!props.DebtId && !props.CurrentDebt;
    const summary = amount > 0 && currency && counterpartName
        ? <>{counterpartName} {isReceivable ? 'nos debe' : 'cobra'} <b className="cff-num">{formatCurrencyAmountWithIssue(amount, currency)}</b></>
        : undefined;

    return (
        <GuideDrawer
            open={props.isOpen}
            size="medium"
            title={title}
            eyebrow={isEdit && props.CurrentDebt
                ? ['Flujo de caja', `Deuda #${props.CurrentDebt.id}`, props.CurrentDebt.settlementId ? `LIQ-${props.CurrentDebt.settlementId}` : undefined].filter(Boolean).join(' · ')
                : 'Flujo de caja'}
            subtitle={summary}
            onDismiss={close}
            closeLabel="Cerrar"
            testId="debt-form-drawer"
            secondaryAction={{ label: 'Cancelar', disabled: props.isSaving }}
            primaryAction={{
                label: isEdit ? 'Guardar cambios' : 'Registrar deuda',
                onClick: save,
                loading: props.isSaving,
                disabled: submitted ? !canSave : false,
            }}
        >
            {loadingEdit ? <div className="cff-root cff-state"><Spinner size="small" /> Cargando deuda…</div> : renderForm()}
        </GuideDrawer>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(DebtFormComponent as any);
